import { t } from "../../i18n";
import {
  CheckCircle2,
  Copy,
  LoaderCircle,
  Monitor,
  Power,
  RefreshCw,
} from "lucide-react";
import type { AgentWorkspaceDefinition } from "@lumen/shared";

export type ConnectedMachine = {
  id: string;
  name: string;
  scope: "platform" | "organization" | "personal";
  platform: string;
  architecture: string;
  status: string;
  folders: Array<{
    id: string;
    name: string;
    enabled: boolean;
    git?: { available: boolean } | null;
  }>;
};

type MachineWorkspace = NonNullable<AgentWorkspaceDefinition["machine"]>;

type Props = {
  workspaces?: MachineWorkspace[];
  machine?: ConnectedMachine;
  enrollment: { token: string; serverUrl: string } | null;
  machineBusy: string | null;
  onRotate?: () => void;
  onRevoke?: () => void;
  onCopy: (value: string, label: string) => void;
};

function statusClass(status: string) {
  return status === "active"
    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
    : status === "offline"
      ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
      : "bg-stone-100 text-stone-500 dark:bg-white/10 dark:text-stone-300";
}

export function ConnectedMachineWorkspaceEditor({
  workspaces = [],
  machine,
  enrollment,
  machineBusy,
  onRotate,
  onRevoke,
  onCopy,
}: Props) {
  if (workspaces.length > 0) {
    const machineWorkspace = workspaces[0]!;
    return (
      <div className="space-y-5">
        <div className="panel space-y-5 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="grid size-10 place-items-center rounded-xl bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300">
                <Monitor size={20} />
              </span>
              <div>
                <p className="text-xs font-semibold tracking-wide text-lumen-600 uppercase">
                  {t("Machine workspace")}
                </p>
                <h2 className="mt-1 text-xl font-bold">
                  {machineWorkspace.name}
                </h2>
                <p className="mt-1 text-sm text-stone-500">
                  {machineWorkspace.platform} · {machineWorkspace.architecture}
                </p>
              </div>
            </div>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${statusClass(machineWorkspace.status)}`}
            >
              {machineWorkspace.status}
            </span>
          </div>
          <div className="grid gap-3">
            {workspaces.map((workspace) => (
              <div
                key={workspace.folderId}
                className="rounded-xl border border-stone-200 p-4 dark:border-white/10"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-stone-400">
                      {t("Shared folder")}
                    </p>
                    <p className="mt-1 font-semibold">{workspace.folderName}</p>
                  </div>
                  <span className="rounded-full bg-stone-100 px-2 py-1 text-[10px] font-semibold text-stone-500 uppercase dark:bg-white/10 dark:text-stone-300">
                    {workspace.status === "active"
                      ? "Available"
                      : workspace.status}
                  </span>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg bg-stone-50 p-3 dark:bg-white/5">
                    <p className="text-xs text-stone-400">Git</p>
                    <p className="mt-1 font-semibold">
                      {workspace.git?.available
                        ? `Available${workspace.git.branch ? ` · ${workspace.git.branch}` : ""}`
                        : "Not available"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-stone-50 p-3 dark:bg-white/5">
                    <p className="text-xs text-stone-400">
                      {t("Working tree")}
                    </p>
                    <p className="mt-1 font-semibold">
                      {workspace.git?.dirty
                        ? "Changes present"
                        : workspace.git?.available
                          ? "Clean"
                          : "—"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm leading-6 text-stone-500">
            {t(
              "This machine is the managed resource. Each shared folder behaves\n            like an agent workspace in the environment selector, while its\n            files, instructions, skills, commands, shell policy, and Git\n            operations stay on the connected computer.",
            )}
          </p>
          {machine && (
            <div className="flex flex-wrap gap-2 border-t border-stone-100 pt-4 dark:border-white/10">
              <button
                className="button text-red-600"
                type="button"
                disabled={machineBusy === machine.id}
                onClick={onRevoke}
              >
                {machineBusy === machine.id && (
                  <LoaderCircle className="animate-spin" size={14} />
                )}
                <Power size={14} /> {t("Revoke machine")}
              </button>
              <button
                className="button"
                type="button"
                disabled={machineBusy === machine.id}
                onClick={onRotate}
              >
                {machineBusy === machine.id && (
                  <LoaderCircle className="animate-spin" size={14} />
                )}
                <RefreshCw size={14} /> {t("Get new enrollment token")}
              </button>
            </div>
          )}
          {enrollment && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 shrink-0" size={17} />
                <div>
                  <strong>{t("New enrollment token ready")}</strong>
                  <p className="mt-1 text-xs opacity-80">
                    {t(
                      "Copy these values into the Lumen Machine app. The token\n                    expires after 15 minutes.",
                    )}
                  </p>
                </div>
              </div>
              <div className="mt-3 grid gap-2">
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 rounded-lg bg-white/70 px-3 py-2 text-xs break-all dark:bg-black/20">
                    {enrollment.serverUrl}
                  </code>
                  <button
                    className="button shrink-0 p-2"
                    type="button"
                    onClick={() => onCopy(enrollment.serverUrl, "Lumen URL")}
                    aria-label={t("Copy Lumen URL")}
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 rounded-lg bg-white/70 px-3 py-2 text-xs break-all dark:bg-black/20">
                    {enrollment.token}
                  </code>
                  <button
                    className="button shrink-0 p-2"
                    type="button"
                    onClick={() => onCopy(enrollment.token, "Enrollment token")}
                    aria-label={t("Copy enrollment token")}
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (machine) {
    return (
      <div className="space-y-5">
        <div className="panel space-y-5 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="grid size-10 place-items-center rounded-xl bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300">
                <Monitor size={20} />
              </span>
              <div>
                <p className="text-xs font-semibold tracking-wide text-lumen-600 uppercase">
                  {t("Connected machine")}
                </p>
                <h2 className="mt-1 text-xl font-bold">{machine.name}</h2>
                <p className="mt-1 text-sm text-stone-500">
                  {machine.platform} · {machine.architecture} · {machine.scope}
                </p>
              </div>
            </div>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${statusClass(machine.status)}`}
            >
              {machine.status}
            </span>
          </div>
          <div className="rounded-xl bg-stone-50 p-4 text-sm dark:bg-white/5">
            <p className="font-semibold">{t("No shared folders yet")}</p>
            <p className="mt-1 text-stone-500">
              {t(
                "Open the Lumen Machine app on this computer to add a folder. It\n              will appear here as a normal agent workspace.",
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-stone-100 pt-4 dark:border-white/10">
            <button
              className="button text-red-600"
              type="button"
              disabled={machineBusy === machine.id}
              onClick={onRevoke}
            >
              {machineBusy === machine.id && (
                <LoaderCircle className="animate-spin" size={14} />
              )}
              <Power size={14} /> {t("Revoke machine")}
            </button>
            <button
              className="button"
              type="button"
              disabled={machineBusy === machine.id}
              onClick={onRotate}
            >
              {machineBusy === machine.id && (
                <LoaderCircle className="animate-spin" size={14} />
              )}
              <RefreshCw size={14} /> {t("Get new enrollment token")}
            </button>
          </div>
          {enrollment && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100">
              <strong>{t("New enrollment token ready")}</strong>
              <p className="mt-1 text-xs opacity-80">
                {t(
                  "Copy these values into the Lumen Machine app. The token expires\n                after 15 minutes.",
                )}
              </p>
              <div className="mt-3 grid gap-2">
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 rounded-lg bg-white/70 px-3 py-2 text-xs break-all dark:bg-black/20">
                    {enrollment.serverUrl}
                  </code>
                  <button
                    className="button shrink-0 p-2"
                    type="button"
                    onClick={() => onCopy(enrollment.serverUrl, "Lumen URL")}
                    aria-label={t("Copy Lumen URL")}
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 rounded-lg bg-white/70 px-3 py-2 text-xs break-all dark:bg-black/20">
                    {enrollment.token}
                  </code>
                  <button
                    className="button shrink-0 p-2"
                    type="button"
                    onClick={() => onCopy(enrollment.token, "Enrollment token")}
                    aria-label={t("Copy enrollment token")}
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-lumen-200 bg-lumen-50/60 p-4 text-sm dark:border-lumen-900/50 dark:bg-lumen-950/20">
      <div className="flex items-start gap-2">
        <Monitor
          className="mt-0.5 shrink-0 text-lumen-700 dark:text-lumen-300"
          size={17}
        />
        <div>
          <strong>{t("Connected machine workspace")}</strong>
          <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
            {t(
              "Save this workspace to create an enrollment. The downloaded Lumen\n            Machine app will let you share local folders under this workspace.",
            )}
          </p>
        </div>
      </div>
      {enrollment && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 shrink-0" size={17} />
            <div>
              <strong>{t("Enrollment ready")}</strong>
              <p className="mt-1 text-xs opacity-80">
                {t(
                  "Copy these values into the downloaded Lumen Machine app. The\n                token expires after 15 minutes.",
                )}
              </p>
            </div>
          </div>
          <div className="mt-4 grid gap-3">
            <div>
              <span className="label">{t("Lumen URL")}</span>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 rounded-lg bg-white/70 px-3 py-2 text-xs break-all dark:bg-black/20">
                  {enrollment.serverUrl}
                </code>
                <button
                  className="button shrink-0 p-2"
                  type="button"
                  onClick={() => onCopy(enrollment.serverUrl, "Lumen URL")}
                  aria-label={t("Copy Lumen URL")}
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <div>
              <span className="label">{t("Enrollment token")}</span>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 rounded-lg bg-white/70 px-3 py-2 text-xs break-all dark:bg-black/20">
                  {enrollment.token}
                </code>
                <button
                  className="button shrink-0 p-2"
                  type="button"
                  onClick={() => onCopy(enrollment.token, "Enrollment token")}
                  aria-label={t("Copy enrollment token")}
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
